

import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../api/axios";
import { runBackgroundRequest } from "../utils/backgroundRequestQueue";

/**
 * StatisticsUnreadContext
 *  • Polls backend unread count for user statistics (ActivityLog)
 *
 * Backend endpoint:
 *   GET /storage/user_statistics_unread_count/
 * Response:
 *   { unread_count: number }
 */

export const StatisticsUnreadContext = createContext(null);

const clampInt = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n));
};

export const formatUnreadBadge = (count) => {
  const n = clampInt(count);
  if (n <= 0) return "";
  // Keep same convention as chat: cap at 999+
  if (n > 999) return "999+";
  return String(n);
};

export const StatisticsUnreadProvider = ({
  children,
  enabled = true,
  intervalMs = 60_000,
}) => {
  const queryClient = useQueryClient();
  const { data, refetch } = useQuery({
    queryKey: ["statistics_unread_count"],
    enabled: !!enabled,
    queryFn: async () => {
      const res = await runBackgroundRequest(() => (
        api.get("/storage/user_statistics_unread_count/", {
          meta: { suppressGlobalError: true },
        })
      ));
      return res?.data || { unread_count: 0 };
    },
    refetchInterval: intervalMs,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const markAllSeen = useCallback(async () => {
    const response = await api.post("/storage/mark_statistics_seen_all/");
    const unreadCount = clampInt(response?.data?.unread_count);
    queryClient.setQueryData(["statistics_unread_count"], {
      unread_count: unreadCount,
    });
    return unreadCount;
  }, [queryClient]);

  const value = useMemo(() => {
    const unreadStatistics = clampInt(data?.unread_count);

    return {
      unreadStatistics,
      formatUnreadBadge,
      markAllSeen,
      refetch,
    };
  }, [data, markAllSeen, refetch]);

  return (
    <StatisticsUnreadContext.Provider value={value}>
      {children}
    </StatisticsUnreadContext.Provider>
  );
};

export const useStatisticsUnread = () => {
  const ctx = useContext(StatisticsUnreadContext);
  return (
    ctx || {
      unreadStatistics: 0,
      formatUnreadBadge,
      markAllSeen: async () => 0,
      refetch: async () => {},
    }
  );
};
